import { useCallback, useEffect, useState } from 'react'

import { config } from '@/config'
import { useMessage, useTranslation, useUserTmpUploader } from '@/core'
import { Camera } from '@/core/native'

import { ImageEditModal } from './ImageEditModal'

/**
 *
 * @description useImageEditor
 * 画像を選択するとトリム編集用のモーダルを表示し、編集後に処理をして必要な値を返すカスタムフック
 *
 * @param {string | undefined} userId ログインユーザーのuserId
 * @param {initialPath} string 初期の画像パス
 * @param {data => Promise<void>} callback 画像保存後にデータ値を使って呼び出すコールバック
 *
 * @returns ItemEditModal モーダルコンポーネントのJSX
 * @returns ModalProps モーダルコンポーネント呼び出し時に渡すprops
 * @returns handleChoseFile 画像を選択し、選択するとモーダルを開く関数
 * @returns handleRemoveFile トリムして一時保存した画像を削除する関数
 * @returns filePathAfterCrop トリム後に画像を表示する用のURL
 *
 * 使い方
 * - ItemEditModalコンポーネントをJSXに配置し、ModalPropsを渡す。
 * - handleChoseFileを呼び出すことで「ファイル選択→選択後モーダル表示」の挙動を実現できる。
 * - 第二引数のcallbackで画像トリム後の挙動を設定できるので、これでフォームの値を変更するなどを行う
 */
export const useImageEditor = ({
  userId,
  callback,
  initialPath = '',
}: {
  userId: string | undefined
  callback: (data: { tmpPath: string; fileUrl: string }) => Promise<void>
  initialPath?: string | undefined
}) => {
  const { t } = useTranslation()
  const { showError } = useMessage()
  const [open, setOpen] = useState(false)

  const { getImage } = Camera
  const { upload } = useUserTmpUploader(userId ?? '')
  const [filePathBeforeCrop, setFilePathBeforeCrop] = useState('')
  const [filePathAfterCrop, setFilePathAfterCrop] = useState('')

  const handleGetImage = useCallback(
    () =>
      getImage(config.upload.mimeTypes.image)
        .then(file => {
          if (file.size > config.upload.limit.image) {
            throw new Error('uploadErrorMaximumSize')
          }
          return file
        })
        .catch(error => {
          if (error.message === 'User denied access to photos') {
            showError(new Error(t('uploadErrorAccessDenied')))
            return
          }
          if (error.message === 'uploadErrorMaximumSize') {
            showError(new Error(t('uploadErrorMaximumSize')))
            return
          }
          if (error.message === 'uploadErrorImageOnly') {
            showError(new Error(t('uploadErrorImageOnly')))
            return
          }
          showError(error)
          return
        }),
    [getImage, showError, t]
  )

  const handleChoseFile = useCallback(async () => {
    const file = await handleGetImage()
    if (!file) return
    setFilePathBeforeCrop(URL.createObjectURL(file))
    setOpen(true)
  }, [handleGetImage])

  const handleRemoveFile = useCallback(() => {
    setFilePathAfterCrop('')
  }, [])

  const handleSave = async (data: { tmpPath: string; fileUrl: string }) => {
    setFilePathAfterCrop(data.fileUrl)
    await callback(data)
    setOpen(false)
  }

  useEffect(() => {
    setFilePathAfterCrop(initialPath)
  }, [initialPath])

  const ModalProps = {
    open,
    src: filePathBeforeCrop ?? '',
    onClose: () => setOpen(false),
    onUploadFile: (file: Blob | File) => upload(file),
    onSave: handleSave,
  }

  return {
    ImageEditModal,
    ModalProps,
    handleChoseFile,
    handleRemoveFile,
    filePathAfterCrop,
  }
}
